import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import type { JSX } from 'react';

// ── Pipeline definitions ────────────────────────────────────────────────────

interface PipelineStage {
  key: string;
  label: string;
  to: string;
}

interface PipelineBranch {
  key: string;
  label: string;
  to: string;
  muted?: boolean;
  after?: string;
}

interface PipelineDef {
  id: string;
  label: string;
  stages: PipelineStage[];
  branch?: PipelineBranch;
  branches?: PipelineBranch[];
}

const PIPELINES: PipelineDef[] = [
  {
    id: 'po',
    label: 'PO Pipeline',
    stages: [
      { key: 'draft', label: 'Draft', to: '/purchasing/purchase-orders?status=draft' },
      {
        key: 'pending_review',
        label: 'Pending Review',
        to: '/purchasing/purchase-orders?status=pending_review',
      },
      { key: 'approved', label: 'Approved', to: '/purchasing/purchase-orders?status=approved' },
      { key: 'ordered', label: 'Ordered', to: '/purchasing/purchase-orders?status=ordered' },
      {
        key: 'awaiting_invoice',
        label: 'Awaiting Invoice',
        to: '/purchasing/purchase-orders?status=awaiting_invoice',
      },
      {
        key: 'processing',
        label: 'Processing',
        to: '/purchasing/purchase-orders?status=processing',
      },
      {
        key: 'invoice_received',
        label: 'Invoice Received',
        to: '/purchasing/purchase-orders?status=invoice_received',
      },
      { key: 'partially_received', label: 'Partially Received', to: '/purchasing/receiving' },
      { key: 'received', label: 'Received', to: '/purchasing/purchase-orders?status=received' },
      { key: 'closed', label: 'Closed', to: '/purchasing/purchase-orders?status=closed' },
    ],
    branches: [
      { key: 'discrepancy', label: 'Discrepancy', to: '/purchasing/discrepancies' },
      {
        key: 'superseded',
        label: 'Superseded',
        to: '/purchasing/purchase-orders?status=superseded',
        muted: true,
      },
    ],
  },
  {
    id: 'fulfillment',
    label: 'Fulfillment',
    stages: [
      { key: 'new_order', label: 'New Order', to: '/fulfillment/orders' },
      { key: 'processing', label: 'Processing', to: '/fulfillment/fulfill' },
      { key: 'packed', label: 'Packed', to: '/fulfillment/pick' },
      { key: 'awaiting_label', label: 'Awaiting Label', to: '/fulfillment/awaiting-label' },
      { key: 'awaiting_pickup', label: 'Awaiting Pickup', to: '/fulfillment/awaiting-pickup' },
      { key: 'shipped', label: 'Shipped', to: '/fulfillment/ship' },
      { key: 'delivered', label: 'Delivered', to: '/fulfillment/orders' },
      {
        key: 'channel_fulfilled',
        label: 'Channel Fulfilled',
        to: '/fulfillment/channel-fulfilled',
      },
    ],
    branches: [
      { key: 'on_hold', label: 'On Hold', to: '/fulfillment/on-hold' },
      { key: 'discrepancy', label: 'Discrepancy', to: '/fulfillment/discrepancies' },
    ],
  },
  {
    id: 'returns',
    label: 'Returns',
    stages: [
      { key: 'initiated', label: 'Initiated', to: '/returns/inspection' },
      { key: 'inspection', label: 'Inspection', to: '/returns/inspection' },
      { key: 'disposition', label: 'Disposition', to: '/returns/disposition' },
      { key: 'refund_issued', label: 'Refund Issued', to: '/returns/disposition' },
      { key: 'closed', label: 'Closed', to: '/returns/disposition' },
    ],
    branch: {
      key: 'discrepancy',
      label: 'Discrepancy',
      to: '/returns/discrepancies',
      after: 'closed',
    },
  },
  {
    id: 'inventory',
    label: 'Inventory',
    stages: [
      { key: 'in_po', label: 'In PO', to: '/purchasing/purchase-orders' },
      { key: 'in_warehouse', label: 'In Warehouse', to: '/inventory?location=at_warehouse' },
      { key: 'fba_inbound', label: 'FBA Inbound', to: '/inventory?location=fba_inbound' },
      { key: 'fba_live', label: 'FBA Live', to: '/inventory?location=fba_live' },
      { key: 'fbm_listed', label: 'FBM Listed', to: '/inventory?location=fbm_listed' },
      { key: 'out_of_stock', label: 'Out of Stock', to: '/inventory?location=out_of_stock' },
    ],
  },
];

// ── Stage pill ──────────────────────────────────────────────────────────────

interface StagePillProps {
  label: string;
  count: number;
  to: string;
  muted?: boolean;
}

function StagePill({ label, count, to, muted }: StagePillProps): JSX.Element {
  const navigate = useNavigate();
  const active = count > 0;
  return (
    <button
      onClick={() => navigate(to)}
      className={clsx(
        'flex items-center gap-1 px-1.5 py-0.5 rounded-md text-2xs transition-colors cursor-pointer select-none whitespace-nowrap',
        'border',
        muted
          ? 'border-border-subtle/50 bg-surface-2/50 text-ink-muted/60 hover:bg-surface-3/50'
          : active
            ? 'border-accent/30 bg-accent/5 text-ink-primary hover:bg-accent/10'
            : 'border-border-subtle bg-surface-2 text-ink-muted hover:bg-surface-3'
      )}
    >
      <span>{label}</span>
      <span
        className={clsx(
          'font-mono text-2xs min-w-[1.25rem] h-[1.125rem] rounded-full flex items-center justify-center px-1',
          active ? 'bg-accent/20 text-accent' : 'bg-surface-4 text-ink-muted'
        )}
      >
        {count}
      </span>
    </button>
  );
}

// ── Pipeline strip ──────────────────────────────────────────────────────────

type StageCounts = Record<string, number>;

interface PipelineStripProps {
  pipeline: PipelineDef;
  counts: StageCounts;
}

function PipelineStrip({ pipeline, counts }: PipelineStripProps): JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <div className="w-[140px] flex-shrink-0 text-right pt-1.5">
        <span className="text-xs text-ink-muted">{pipeline.label}</span>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-1 flex-wrap">
          {pipeline.stages.map((stage, i) => (
            <div key={stage.key} className="flex items-center gap-1">
              {i > 0 && <ChevronRight size={10} className="text-ink-muted flex-shrink-0 -mx-0.5" />}
              <StagePill label={stage.label} count={counts[stage.key] || 0} to={stage.to} />
            </div>
          ))}
        </div>
        {/* Single branch (e.g. Returns → Discrepancy, PO → Discrepancy) */}
        {pipeline.branch && (
          <div className="flex items-center gap-1 mt-1 ml-4">
            <span className="text-ink-muted text-xs">↳</span>
            <StagePill
              label={pipeline.branch.label}
              count={counts[pipeline.branch.key] || 0}
              to={pipeline.branch.to}
            />
          </div>
        )}
        {/* Multiple branches (e.g. Fulfillment → On Hold, Discrepancy) */}
        {pipeline.branches?.map((b) => (
          <div key={b.key} className="flex items-center gap-1 mt-1 ml-4">
            <span className="text-ink-muted text-xs">↳</span>
            <StagePill label={b.label} count={counts[b.key] || 0} to={b.to} muted={b.muted} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── PipelineStrips ──────────────────────────────────────────────────────────

interface PipelineStripsProps {
  counts?: Record<string, StageCounts>;
}

export default function PipelineStrips({ counts = {} }: PipelineStripsProps): JSX.Element {
  return (
    <div className="arbi-card p-4 space-y-3">
      {PIPELINES.map((pipeline) => (
        <PipelineStrip key={pipeline.id} pipeline={pipeline} counts={counts[pipeline.id] || {}} />
      ))}
    </div>
  );
}
